import { isNumber, isString } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { ApiOrgProduct, getScanInlineProductFromOrgProduct } from '@kaiizen/kai-pro-api';
import {
  getEffectiveScanValue,
  getSkuIdFromSkuOption,
  getTotalCostIfDefined,
  getUpdatedScanValue,
  ScanLineItem,
  ScanLineItemOptimized,
  ScanTemplateLineItem,
  ScanTemplateOption,
  ScanTemplateValue,
  ScanValue,
} from '@kaiizen/kai-scan-template-system';
import React, { useCallback, useRef, useState } from 'react';

import { formatUsd, getLineItemFields, getLineItemModifiers, type LineItemField, presentQuantityLabel } from '@/Client/Presenters';
import {
  ScanDataMutators,
  useNavigateToScanFocusPanelPage,
  useOrgProduct,
  useScanEditingContextSlice,
} from '@/Client/State';
import { useConfirmationModal } from '@/Client/State/ConfirmationModalContext';
import { TYPOGRAPHY2 } from '@/Client/Styles';
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import {
  KEBAB_MENU_OPEN_RAISE_STYLE,
  useTooltip,
} from '@/Client/Widgets';
import {
  FocusedApproveCatalogPriceChange,
  getLineItemTotalAtPartsPrice,
  shouldShowApproveCatalogPriceChange,
} from './FocusedApproveCatalogPriceChange';
import { FocusedLineItemChangeOrderStrip, useLineItemRowHasChangeOrder } from './FocusedLineItemChangeOrder';
import { getMeasurementIsTaken, useMeasurementTooltipContent } from './FocusedLineItemMeasurementTooltip';
import { FocusedLineItemOutOfStockStrip, useOrgProductIsOutOfStock } from './FocusedLineItemOutOfStock';
import { FocusedLineItemProductOverrideStrip } from './FocusedLineItemProductOverride';
import { FocusedLineItemProductPhoto } from './FocusedLineItemProductPhoto';
import { FocusedLineItemStatusMenu } from './FocusedLineItemStatusMenu';
import { FocusedLineItemInstructionsTab, FocusedLineItemTab } from './FocusedLineItemTab';
import { FocusedOrgProductFlyout } from './FocusedOrgProductFlyout';
import { FocusedProductSearchDialog } from './FocusedProductSearchDialog';
import { getScanValueOverridesRealDefault, SCAN_VALUE_OVERRIDE_HOST_ATTRIBUTE } from './FocusedScanValueDefaultCaption';
import { FocusedScanValuesTooltipTable } from './FocusedScanValuesTooltipTable';
import { FocusedScanValueView } from './FocusedScanValueView';
import {
  FOCUSED_LINE_ITEM_ADD_BUTTON,
  FOCUSED_LINE_ITEM_FIELD,
  FOCUSED_LINE_ITEM_FIELD_LABEL,
  FOCUSED_LINE_ITEM_FIELD_OVERRIDDEN,
  FOCUSED_LINE_ITEM_FIELDS,
  FOCUSED_LINE_ITEM_FLAGGED,
  FOCUSED_LINE_ITEM_FLAGGED_ERROR,
  FOCUSED_LINE_ITEM_PRODUCT,
  FOCUSED_LINE_ITEM_PRODUCT_BRAND,
  FOCUSED_LINE_ITEM_PRODUCT_META,
  FOCUSED_LINE_ITEM_PRODUCT_NAME,
  FOCUSED_LINE_ITEM_PRODUCT_OPENS_PRODUCT,
  FOCUSED_LINE_ITEM_PRODUCT_PHOTO_SLOT,
  FOCUSED_LINE_ITEM_PRODUCT_SKU,
  FOCUSED_LINE_ITEM_RIGHT_COLUMNS,
  FOCUSED_LINE_ITEM_ROW,
  FOCUSED_LINE_ITEM_ROW_ADDABLE,
  FOCUSED_LINE_ITEM_ROW_ADDED,
  FOCUSED_LINE_ITEM_ROW_WITH_CAPTIONS,
  FOCUSED_LINE_ITEM_ROW_WITH_TABS,
  FOCUSED_LINE_ITEM_STRIPS,
  FOCUSED_LINE_ITEM_STRIPS_ERROR,
  FOCUSED_LINE_ITEM_TAB_MODIFIER,
  FOCUSED_LINE_ITEM_TAB_WARN,
  FOCUSED_LINE_ITEM_TABS,
  FOCUSED_LINE_ITEM_TOTAL,
  FOCUSED_LINE_ITEM_TOTAL_SLOT,
  FOCUSED_LINE_ITEM_TOTAL_WIDTH,
  FOCUSED_LINE_ITEM_TOTAL_ZERO,
  FOCUSED_LINE_ITEM_VALUE_BOX,
  FOCUSED_LINE_ITEM_VALUE_BOX_CHANGED,
  FOCUSED_LINE_ITEM_VALUE_BOX_EDITED_ELSEWHERE,
  FOCUSED_LINE_ITEM_VALUE_BOX_EMPTY,
  FOCUSED_LINE_ITEM_VALUE_BOX_ON_CANDIDATE,
  FOCUSED_LINE_ITEM_VALUE_LINK,
  LINE_ITEM_ADD_BUTTON_ATTRIBUTE,
  LINE_ITEM_ZERO_TOTAL_ATTRIBUTE,
} from './focusedTaskCardStyles';

const LINE_ITEM_STYLE = css`
  label: LINE_ITEM_STYLE;
  display: flex;
  flex-direction: column;
`;

const LINE_ITEM_TITLE_DESCRIPTION_STYLE = css`
  label: LINE_ITEM_TITLE_DESCRIPTION_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  line-height: 1.4;
  max-height: calc(1.4em * 3);
`;

/**
 * The tabs along the top of a line item row naming its overrides: its product's, then its values'. They lead the row's
 * tabs, ahead of its instructions and then its modifiers, which classify the line rather than flag it.
 */
const getOverrideTabs = (productOverridden: boolean, valuesOverridden: boolean) =>
  [
    {
      shown: productOverridden,
      label: 'Product override',
      testId: 'focused-line-item-product-override-tab',
      explanationTitle: 'Product Override',
      explanation: "This line's product replaces the one its template names.",
      hint: 'Restore the original or change the product below.',
    },
    {
      shown: valuesOverridden,
      label: 'Override',
      testId: 'focused-line-item-override-tab',
      explanationTitle: 'Override',
      explanation: 'Values on this line have been changed from their defaults.',
      hint: 'Each shows its default beneath it; click it to reset.',
    },
  ].filter(({ shown }) => shown);

/** Each value column's width at full row width, and its cell's test id. */
const VALUE_COLUMNS = {
  measurement: { width: 84, testId: 'focused-line-item-measurement-cell' },
  quantity: { width: 62, testId: 'focused-line-item-qty-cell' },
  price: { width: 86, testId: 'focused-line-item-parts-cell' },
  laborPrice: { width: 86, testId: 'focused-line-item-labor-cell' },
} as const;

type ValueColumnKey = keyof typeof VALUE_COLUMNS;

const isValueColumn = (field: LineItemField): field is LineItemField & { key: ValueColumnKey } => field.key in VALUE_COLUMNS;

/**
 * The Add button's amount. Everywhere else amounts show to the cent; the button's slot is too
 * narrow for four-figure amounts with cents, so those alone round to the dollar.
 */
const presentAddAmount = (amount: number) => amount >= 1000 ? formatUsd(amount, true) : formatUsd(amount, false, true);

const LineItemColumn = ({ label, width, testId, overridden, children }: {
  label: string;
  width: number;
  testId?: string;
  /** The field's value overrides its default: see FOCUSED_LINE_ITEM_FIELD_OVERRIDDEN. */
  overridden?: boolean;
  children: React.ReactNode;
}) => (
  <div css={[FOCUSED_LINE_ITEM_FIELD, overridden && FOCUSED_LINE_ITEM_FIELD_OVERRIDDEN]} style={{ width }} data-testid={testId}>
    <div css={FOCUSED_LINE_ITEM_FIELD_LABEL}>{label}</div>
    {children}
  </div>
);

export const FocusedInlineLineItemEditor = React.memo(({
  lineItemTemplate,
  optionTemplate,
  lineItem,
  taskId,
  groupId,
  optionId,
  scanDataMutators,
  preview,
  optionExists,
}: {
  lineItemTemplate: ScanTemplateLineItem;
  optionTemplate: ScanTemplateOption;
  /** The line item's scan data, or its vivified preview when `preview` is set. */
  lineItem?: ScanLineItemOptimized;
  taskId: string;
  groupId: string;
  optionId?: string;
  scanDataMutators: ScanDataMutators;
  /**
   * The line item is not in the scan, so not in the scope; `lineItem` (when present) is a calculated
   * preview of it as it would be added.
   */
  preview?: boolean;
  /** Whether the parent option is in the scan data. */
  optionExists: boolean;
}) => {
  const { scanIsEditable, enabledTaskModifiers, hasEnabledTaskModifiers, knownTaskModifiers } = useScanEditingContextSlice([
    'scanIsEditable',
    'enabledTaskModifiers',
    'hasEnabledTaskModifiers',
    'knownTaskModifiers',
  ]);
  const lineItemRef = useRef<HTMLDivElement>(null);
  const navigateToScanFocusPanelPage = useNavigateToScanFocusPanelPage();

  const lineItemInScanData = !!lineItem && !preview;

  const totalCost = getTotalCostIfDefined(lineItem);
  const isScopeStagePickable = !!lineItemTemplate.useScopeStageSkus;

  const values = lineItem?.values ?? {};
  const valueTemplates = lineItemTemplate.values ?? {};

  const measurementTemplate = valueTemplates.measurement;
  const quantityTemplate = valueTemplates.quantity;
  const priceTemplate = valueTemplates.price;
  const laborPriceTemplate = valueTemplates.laborPrice;

  const isMeasured = !!measurementTemplate;
  // an equation-driven measurement reads from real measurements; the Measurements tab is its one source of truth
  const measurementIsEquationDriven = isString(measurementTemplate?.value);
  // an equation-driven measurement the room hasn't been measured for yet reads as a dash, its tooltip naming what to fill in
  const measurementIsMissing = measurementIsEquationDriven && !getMeasurementIsTaken(getEffectiveScanValue(values.measurement));

  const effectiveQuantity = getEffectiveScanValue(values.quantity);
  const rowReadsAdded = !!lineItem && !preview;
  // A candidate line must be explicitly added (the Add button) before any of its fields accept edits.
  const fieldsEditable = scanIsEditable && rowReadsAdded;
  const canAdd = scanIsEditable && !rowReadsAdded;

  const [firstSkuOption] = lineItemTemplate.skuIdOptions ?? [];
  const templateSkuId = firstSkuOption && getSkuIdFromSkuOption(firstSkuOption);
  const skuId = lineItem?.skuId ?? templateSkuId;
  const { orgProduct } = useOrgProduct(skuId);
  // the template's own product, for presenting and resetting a line that has swapped it out
  const { orgProduct: templateOrgProduct } = useOrgProduct(templateSkuId);
  const skuAttached = !!skuId;

  const [productFlyoutOpen, setProductFlyoutOpen] = useState(false);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const canPickProduct = isScopeStagePickable && scanIsEditable;

  /** Routes updates to the right mutator for the row's state: vivify the option, vivify the line item, or update in place. */
  const applyLineItemUpdates = useCallback((updates: Partial<ScanLineItem>) => {
    if (!scanIsEditable) return;

    if (!optionExists || !optionId) {
      scanDataMutators.addOption({
        groupId,
        taskId,
        optionTemplateId: optionTemplate.id,
        addLineItemTemplateId: lineItemTemplate.id,
        addLineItemInitializers: updates,
      });
    } else if (!lineItemInScanData) {
      scanDataMutators.addLineItem({ groupId, taskId, optionId, lineItemTemplateId: lineItemTemplate.id, initializers: updates });
    } else scanDataMutators.updateLineItem(groupId, taskId, optionId, lineItem!.id, updates);
  }, [scanIsEditable, optionExists, lineItemInScanData, lineItem, groupId, taskId, optionId, lineItemTemplate.id, optionTemplate.id, scanDataMutators]);

  // Add puts in exactly what the preview shows: a non-measured line item without a default quantity above zero previews
  // at a set quantity of one; any other goes in as it is (see getTaskOptimizedPreview).
  const previewQuantity = values.quantity?.setValue;
  const addLineItemToScope = useCallback(() => applyLineItemUpdates(isNumber(previewQuantity) ? { values: { quantity: { setValue: previewQuantity } } } : {}), [
    applyLineItemUpdates,
    previewQuantity,
  ]);

  /** Remove takes the line item out of the scan data outright, whatever its kind; its template's preview takes its place in the row. */
  const removeLineItem = useCallback(() => {
    if (!scanIsEditable || !lineItemInScanData || !optionId) return;
    scanDataMutators.deleteLineItem(groupId, taskId, optionId, lineItem!.id);
  }, [scanIsEditable, lineItemInScanData, lineItem, groupId, taskId, optionId, scanDataMutators]);

  const rowLabel = orgProduct?.title ?? lineItemTemplate.name;
  const confirm = useConfirmationModal();
  // bumped when a quantity edit toward zero is cancelled, remounting the input back to the real quantity
  const [quantityInputResetCount, setQuantityInputResetCount] = useState(0);

  // Removing is how a line item leaves the scope, so taking its quantity to zero asks to remove it instead.
  const updateValue = useCallback((key: string, newValue: ScanValue) => {
    const newEffectiveValue = getEffectiveScanValue(newValue);
    if (key === 'quantity' && lineItemInScanData && isNumber(newEffectiveValue) && newEffectiveValue <= 0) {
      confirm({
        title: 'Remove line item',
        message: `Do you want to remove ${rowLabel}?`,
        confirmLabel: 'Remove',
        onConfirm: removeLineItem,
        onCancel: () => setQuantityInputResetCount(count => count + 1),
      });
      return;
    }
    applyLineItemUpdates({ values: { [key]: newValue } });
  }, [applyLineItemUpdates, confirm, lineItemInScanData, removeLineItem, rowLabel]);

  // how the line's total is calculated, value by value, from the total's amount (or, not yet added, the Add button's)
  const totalCalculationTooltip = useTooltip(
    Object.keys(valueTemplates).length > 0 && <FocusedScanValuesTooltipTable values={values} valueTemplates={valueTemplates} />,
    { title: 'Calculations', maxWidth: 540 },
  );
  const measurementTooltip = useTooltip(
    useMeasurementTooltipContent(groupId, measurementTemplate, !measurementIsMissing),
    { title: 'Measurement' },
  );

  const navigateToMeasurements = useCallback(() => {
    if (!scanDataMutators.scanId) return;
    navigateToScanFocusPanelPage({ scanId: scanDataMutators.scanId, panel: 'measurements', groupId });
  }, [navigateToScanFocusPanelPage, scanDataMutators.scanId, groupId]);

  /* The modifiers on a line in the scope, named by the org. This was two fixed tags, so a
     Display modifier an org minted itself never reached the row (KAIDEV-4206). Read from the
     known list rather than the enabled one: disabling a modifier stops it being offered in the
     kebab, it does not blank the lines already carrying it. */
  const lineItemModifiers = rowReadsAdded ? getLineItemModifiers(lineItem?.tags, knownTaskModifiers) : [];

  const quantityLabel = presentQuantityLabel(quantityTemplate);

  // what each value is, heading the tooltips about it; labor is priced per the unit it's multiplied by: the measurement's, if measured
  const laborUnit = (isMeasured ? measurementTemplate.type : quantityTemplate?.type) ?? 'count';
  const valueTitles = {
    measurement: 'Measurement',
    quantity: quantityTemplate?.type === 'count' ? 'Quantity' : quantityLabel,
    price: 'Price per Unit',
    laborPrice: `Labor Price per ${laborUnit === 'count' ? 'Unit' : laborUnit}`,
  };

  /**
   * Puts a product on the line at its current price, set on the line's price. Picking, replacing, resetting to the
   * template's, and accepting a price change.
   */
  const selectProduct = useCallback((selectedOrgProduct: ApiOrgProduct) => {
    if (!scanIsEditable) return;
    applyLineItemUpdates({
      skuId: selectedOrgProduct.id,
      skuDetails: getScanInlineProductFromOrgProduct(selectedOrgProduct),
      ...(priceTemplate && isNumber(selectedOrgProduct.price)
        ? { values: { price: getUpdatedScanValue(priceTemplate, values.price, selectedOrgProduct.price) } }
        : {}),
    });
    setProductSearchOpen(false);
  }, [scanIsEditable, applyLineItemUpdates, priceTemplate, values.price]);

  const lineItemId = lineItemInScanData ? lineItem!.id : undefined;
  const changeOrderLocation = { groupId, taskId, optionTemplateId: optionTemplate.id, lineItemTemplateId: lineItemTemplate.id, lineItemId };
  const rowHasChangeOrder = useLineItemRowHasChangeOrder(changeOrderLocation);

  const defaultPrice = orgProduct?.price;

  const catalogPriceNeedsApproval = shouldShowApproveCatalogPriceChange({
    scanIsEditable,
    defaultPrice,
    lineItemId,
    optionId,
    lineItemPrice: values.price,
  });

  const approveCatalogPriceChange = useCallback(() => orgProduct && selectProduct(orgProduct), [orgProduct, selectProduct]);

  // For testing: moves the line's price 5% off its org product's price, up or down at random, so the row reads as a price change.
  const fakeCatalogPriceChange = useCallback(() => {
    if (!lineItemId || !isNumber(defaultPrice) || !priceTemplate) return;
    const fakedPrice = Math.round(defaultPrice * (Math.random() < 0.5 ? 0.95 : 1.05) * 100) / 100;
    applyLineItemUpdates({ values: { price: getUpdatedScanValue(priceTemplate, values.price, fakedPrice) } });
  }, [applyLineItemUpdates, defaultPrice, lineItemId, priceTemplate, values.price]);
  const canFakeCatalogPriceChange = scanIsEditable && isNumber(defaultPrice) && !!priceTemplate;

  // a catalog price change only matters on a row that's actually in the scope
  const showCatalogPriceChange = rowReadsAdded && catalogPriceNeedsApproval && isNumber(defaultPrice);
  // the row presents the line item as it is; the strip under it says what the catalog price moved to, and the total at it
  const totalAtCatalogPrice = showCatalogPriceChange ? getLineItemTotalAtPartsPrice(lineItem!, defaultPrice) : undefined;
  // For testing: the product this row pretends is out of stock, until toggled off; a replacement product reads in stock again.
  const [fakeOutOfStockSkuId, setFakeOutOfStockSkuId] = useState<string>();
  const fakingOutOfStock = !!orgProduct && fakeOutOfStockSkuId === skuId;
  const toggleFakeOutOfStock = useCallback(() => setFakeOutOfStockSkuId(fakingOutOfStock ? undefined : skuId), [fakingOutOfStock, skuId]);
  const orgProductIsOutOfStock = useOrgProductIsOutOfStock(orgProduct);
  const templateOrgProductIsOutOfStock = useOrgProductIsOutOfStock(templateOrgProduct);
  const showOutOfStock = rowReadsAdded && (orgProductIsOutOfStock || fakingOutOfStock);
  // any out-of-stock line can shop for a replacement, whether or not its template lets the scope pick products
  const canFindReplacement = showOutOfStock && scanIsEditable;
  // the fields shown as inputs whose values override their defaults, each captioned with the default it replaced
  const overriddenFields = {
    measurement: fieldsEditable && !measurementIsEquationDriven && getScanValueOverridesRealDefault(values.measurement),
    // an unmeasured line's quantity is the user's to set, never an override of anything
    quantity: fieldsEditable && isMeasured && getScanValueOverridesRealDefault(values.quantity),
    price: fieldsEditable && !skuAttached && !showCatalogPriceChange && getScanValueOverridesRealDefault(values.price),
    laborPrice: fieldsEditable && getScanValueOverridesRealDefault(values.laborPrice),
    // a product other than the template's, unless the template leaves the product to the scope
    product: fieldsEditable && !!templateSkuId && skuId !== templateSkuId && !lineItemTemplate.useScopeStageSkus,
  };
  const { product: productOverridden, ...overriddenValues } = overriddenFields;
  const rowHasValueOverride = Object.values(overriddenValues).some(Boolean);
  const rowHasOverride = rowHasValueOverride || productOverridden;
  const rowIsFlagged = showCatalogPriceChange || showOutOfStock || rowHasOverride || rowHasChangeOrder;
  // the template line item's description is the line's instructions, on any row: they apply before it's added too
  const instructions = lineItemTemplate.description?.trim();
  const overrideTabs = getOverrideTabs(productOverridden, rowHasValueOverride);
  const rowHasTabs = overrideTabs.length > 0 || !!instructions || lineItemModifiers.length > 0;

  // the preview costs what adding the line item will (see getTaskOptimizedPreview)
  const addAmount = totalCost ?? 0;

  // A click anywhere in the product section shops for a product when the scope picks it, or it's out of stock or
  // overridden; a fixed, in-stock product opens its details instead.
  const productSectionShopsForProduct = canPickProduct || canFindReplacement || (scanIsEditable && productOverridden);
  const openProduct = productSectionShopsForProduct
    ? () => setProductSearchOpen(true)
    : orgProduct
    ? () => setProductFlyoutOpen(true)
    : undefined;
  const openProductOnEnterOrSpace = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    openProduct?.();
  };

  const valueBoxStyle = [FOCUSED_LINE_ITEM_VALUE_BOX, !rowReadsAdded && FOCUSED_LINE_ITEM_VALUE_BOX_ON_CANDIDATE];
  /** A read-only value in its recessed box; a column the line item has no value for holds its place with a dash. */
  const renderValueBox = (presented: string | undefined) =>
    presented
      ? <div css={valueBoxStyle}>{presented}</div>
      : <div css={[valueBoxStyle, FOCUSED_LINE_ITEM_VALUE_BOX_EMPTY]}>{'—'}</div>;
  // a candidate that isn't measured has no quantity until it's added
  const presentedQuantity = rowReadsAdded || isMeasured
    ? isNumber(effectiveQuantity) ? effectiveQuantity.toLocaleString() : undefined
    : undefined;
  /** An editable value's input, bare: its LineItemField carries the label. */
  const renderValueInput = (valueKey: keyof typeof valueTitles, templateValue: ScanTemplateValue, presentsOverride?: boolean) => (
    <FocusedScanValueView
      noLabel
      valueKey={valueKey}
      tooltipTitle={valueTitles[valueKey]}
      presentsOverride={presentsOverride}
      value={values[valueKey]}
      templateValue={templateValue}
      updateValue={updateValue}
      style='taskCardField'
      width='100%'
      size='small'
      backgroundColor={COLORS.white}
      step={valueKey === 'quantity' ? 1 : undefined}
    />
  );

  const lineItemFields = getLineItemFields(valueTemplates);
  const valueFields = lineItemFields.filter(isValueColumn);
  const totalField = lineItemFields.find(({ key }) => key === 'totalCost')!;
  /** A value column's content: an equation-driven measurement's link, an editable value's input, or its value in its box. */
  const renderValueCell = (key: ValueColumnKey, presented: string | undefined) => {
    switch (key) {
      case 'measurement':
        return measurementIsEquationDriven
          ? (
            <button
              css={[valueBoxStyle, FOCUSED_LINE_ITEM_VALUE_BOX_EDITED_ELSEWHERE, FOCUSED_LINE_ITEM_VALUE_LINK]}
              type='button'
              {...measurementTooltip.anchorProps}
              data-testid='focused-line-item-measurement-link'
              onClick={navigateToMeasurements}
            >
              {measurementIsMissing ? '—' : presented}
              {measurementTooltip.tooltip}
            </button>
          )
          : fieldsEditable
          ? renderValueInput('measurement', valueTemplates.measurement!)
          : renderValueBox(presented);
      case 'quantity':
        return fieldsEditable
          ? <React.Fragment key={quantityInputResetCount}>{renderValueInput('quantity', valueTemplates.quantity!, isMeasured)}</React.Fragment>
          : renderValueBox(presentedQuantity);
      case 'price':
        return showCatalogPriceChange
          ? <div css={[valueBoxStyle, FOCUSED_LINE_ITEM_VALUE_BOX_CHANGED]} data-testid='focused-line-item-parts-price'>{presented}</div>
          // with a SKU attached the parts price comes from the product
          : fieldsEditable && !skuAttached
          ? renderValueInput('price', valueTemplates.price!)
          : renderValueBox(presented);
      case 'laborPrice':
        return fieldsEditable ? renderValueInput('laborPrice', valueTemplates.laborPrice!) : renderValueBox(presented);
    }
  };

  const rowFrameIsError = showOutOfStock || rowHasOverride || rowHasChangeOrder;
  // the row's action strips, one priority at a time: out of stock alone, else a price change alone, else every other that applies
  const actionStrips = showOutOfStock
    ? [
      <FocusedLineItemOutOfStockStrip
        key='out-of-stock'
        onFindReplacement={canFindReplacement ? () => setProductSearchOpen(true) : undefined}
      />,
    ]
    : showCatalogPriceChange
    ? [
      <FocusedApproveCatalogPriceChange
        key='price-change'
        lineItemPrice={values.price}
        catalogUsd={defaultPrice}
        totalAtCatalogPrice={totalAtCatalogPrice!}
        onApprove={approveCatalogPriceChange}
      />,
    ]
    : [
      productOverridden && (
        <FocusedLineItemProductOverrideStrip
          key='product-override'
          originalTitle={templateOrgProduct?.title}
          originalIsOutOfStock={templateOrgProductIsOutOfStock}
          onRestoreOriginal={templateOrgProduct ? () => selectProduct(templateOrgProduct) : undefined}
          onChangeProduct={() => setProductSearchOpen(true)}
        />
      ),
    ].filter(Boolean);
  const strips = rowHasChangeOrder ? [...actionStrips, <FocusedLineItemChangeOrderStrip key='change-order' {...changeOrderLocation} />] : actionStrips;

  return (
    <div
      css={[LINE_ITEM_STYLE, rowIsFlagged && FOCUSED_LINE_ITEM_FLAGGED, rowFrameIsError && FOCUSED_LINE_ITEM_FLAGGED_ERROR]}
      ref={lineItemRef}
    >
      <div
        css={[
          FOCUSED_LINE_ITEM_ROW,
          rowReadsAdded && FOCUSED_LINE_ITEM_ROW_ADDED,
          canAdd && FOCUSED_LINE_ITEM_ROW_ADDABLE,
          rowHasTabs && FOCUSED_LINE_ITEM_ROW_WITH_TABS,
          rowHasValueOverride && FOCUSED_LINE_ITEM_ROW_WITH_CAPTIONS,
          KEBAB_MENU_OPEN_RAISE_STYLE,
        ]}
        {...{ [SCAN_VALUE_OVERRIDE_HOST_ATTRIBUTE]: true }}
        data-testid='focused-line-item-row'
      >
        {rowHasTabs && (
          <div css={FOCUSED_LINE_ITEM_TABS} data-testid='focused-line-item-tabs'>
            {overrideTabs.map(({ shown, label, ...tab }) => (
              <FocusedLineItemTab key={tab.testId} variantStyle={FOCUSED_LINE_ITEM_TAB_WARN} {...tab}>{label}</FocusedLineItemTab>
            ))}
            {instructions && <FocusedLineItemInstructionsTab instructions={instructions} />}
            {lineItemModifiers.map(({ lineItemTag, displayName }) => (
              <FocusedLineItemTab
                key={lineItemTag}
                variantStyle={FOCUSED_LINE_ITEM_TAB_MODIFIER}
                testId='focused-line-item-modifier-tab'
                explanationTitle='Modifier'
                explanation={`This line is marked ${displayName}.`}
                hint={"Set or clear it from the line's ⋮ menu."}
              >
                {displayName}
              </FocusedLineItemTab>
            ))}
          </div>
        )}
        <div
          css={[FOCUSED_LINE_ITEM_PRODUCT, openProduct && FOCUSED_LINE_ITEM_PRODUCT_OPENS_PRODUCT]}
          data-testid='focused-line-item-product-cell'
          onClick={openProduct}
          {...openProduct && { role: 'button', tabIndex: 0, onKeyDown: openProductOnEnterOrSpace }}
        >
          {/* the photo's place is kept on every row; the image may change, and a fresh one gets a fresh chance to load */}
          <div css={FOCUSED_LINE_ITEM_PRODUCT_PHOTO_SLOT}>
            <FocusedLineItemProductPhoto key={orgProduct?.image} image={orgProduct?.image} overridden={productOverridden} />
          </div>
          <div css={FOCUSED_LINE_ITEM_PRODUCT_META} data-testid='focused-line-item-product-name-block'>
            {orgProduct?.brand && <div css={FOCUSED_LINE_ITEM_PRODUCT_BRAND}>{orgProduct.brand}</div>}
            {orgProduct?.title
              ? <div css={FOCUSED_LINE_ITEM_PRODUCT_NAME}>{orgProduct.title}</div>
              // without a product, the item name is the block's first line and reads as a label
              : <div css={FOCUSED_LINE_ITEM_PRODUCT_BRAND}>{lineItemTemplate.name}</div>}
            {canPickProduct && !orgProduct && <div css={LINE_ITEM_TITLE_DESCRIPTION_STYLE}>{'Tap to choose a product'}</div>}
            {orgProduct?.thdStoreSku && <div css={FOCUSED_LINE_ITEM_PRODUCT_SKU}>{orgProduct.thdStoreSku}</div>}
          </div>
        </div>

        <div css={FOCUSED_LINE_ITEM_RIGHT_COLUMNS} data-testid='focused-line-item-right-columns'>
          <div css={FOCUSED_LINE_ITEM_FIELDS}>
            {valueFields.map(({ key, label, present }) => (
              <LineItemColumn key={key} label={label} width={VALUE_COLUMNS[key].width} testId={VALUE_COLUMNS[key].testId} overridden={overriddenFields[key]}>
                {renderValueCell(key, present(lineItem ?? {}))}
              </LineItemColumn>
            ))}
          </div>

          <div css={FOCUSED_LINE_ITEM_TOTAL_SLOT} data-testid='focused-line-item-total-cell'>
            <LineItemColumn label={totalField.label} width={FOCUSED_LINE_ITEM_TOTAL_WIDTH}>
              {rowReadsAdded
                ? (
                  <div css={FOCUSED_LINE_ITEM_TOTAL} {...totalCalculationTooltip.anchorProps}>
                    {totalField.present(lineItem ?? {})}
                  </div>
                )
                : (
                  // not in the scope, the line costs nothing yet: the amount Add would put in rides on the button
                  <div css={[FOCUSED_LINE_ITEM_TOTAL, FOCUSED_LINE_ITEM_TOTAL_ZERO]} {...{ [LINE_ITEM_ZERO_TOTAL_ATTRIBUTE]: true }}>
                    {formatUsd(0, false, true)}
                  </div>
                )}
              {canAdd && (
                <button
                  css={FOCUSED_LINE_ITEM_ADD_BUTTON}
                  type='button'
                  data-testid='focused-line-item-add-button'
                  onClick={addLineItemToScope}
                  {...{ [LINE_ITEM_ADD_BUTTON_ATTRIBUTE]: true }}
                  {...totalCalculationTooltip.anchorProps}
                >
                  {addAmount > 0 ? `Add ${presentAddAmount(addAmount)}` : 'Add'}
                </button>
              )}
              {totalCalculationTooltip.tooltip}
            </LineItemColumn>
          </div>
        </div>

        {/* out of flow, pinned to the row's right edge, in the space the total slot reserves for it */}
        {rowReadsAdded && (
          <FocusedLineItemStatusMenu
            label={rowLabel}
            lineItem={lineItem}
            groupId={groupId}
            taskId={taskId}
            optionId={optionId}
            scanDataMutators={scanDataMutators}
            enabledTaskModifiers={enabledTaskModifiers}
            isEditable={scanIsEditable && hasEnabledTaskModifiers}
            removeLineItem={scanIsEditable ? removeLineItem : undefined}
            fakeCatalogPriceChange={canFakeCatalogPriceChange ? fakeCatalogPriceChange : undefined}
            fakingOutOfStock={fakingOutOfStock}
            toggleFakeOutOfStock={orgProduct ? toggleFakeOutOfStock : undefined}
          />
        )}
      </div>
      {strips.length > 0 && <div css={[FOCUSED_LINE_ITEM_STRIPS, rowFrameIsError && FOCUSED_LINE_ITEM_STRIPS_ERROR]}>{strips}</div>}
      <FocusedOrgProductFlyout
        orgProduct={orgProduct || null}
        open={productFlyoutOpen}
        onClose={() => setProductFlyoutOpen(false)}
      />
      {productSectionShopsForProduct && (
        <FocusedProductSearchDialog
          isOpen={productSearchOpen}
          onClose={() => setProductSearchOpen(false)}
          onAdd={selectProduct}
          currentProduct={orgProduct}
        />
      )}
    </div>
  );
});
